import Vue from 'vue'
import Router from 'vue-router'
import Error404 from '@/components/errors/Error404'
import MainLayout from '@/components/home/MainLayout'
import Home from '@/components/home/Home'
import Error401 from '@/components/errors/Error401'
import { handleUnauthRoute, handleAuthRoute } from './authGuard'

const LoginPage = () => import('@/components/login/login.vue')
const TaskListHome = () => import('@/components/task-lists/TaskListHome')
const MyHoney = () => import('@/components/my-honey/MyHoney')
const BotScheduler = () => import('@/components/bot-scheduler/BotScheduler')
const Executions = () => import('@/components/execution/Execution')
const DMSHome = () => import('@/components/dms/DMSHome')
const MyTulip = () => import('@/components/admin/MyTulip')
const TulipLiveness = () => import('@/components/admin/TulipLiveness')
const SelfServiceList = () =>
  import('@/components/self-service/SelfServiceList.vue')
const WorkflowList = () =>
  import('@/components/workflow-management/WorkflowList')
const WorkflowCard = () =>
  import('@/components/workflow-management/WorkflowCard')
const WorkflowDesigner = () =>
  import('@/components/workflow-management/WorkflowDesigner')
const BotList = () => import('@/components/bot/BotList')
const BotCard = () => import('@/components/bot/BotCard')
const EmailList = () => import('@/components/email/EmailList.vue')
const HoneyFormList = () => import('@/components/honey-form/HoneyFormList')
const HoneyFormDesigner = () =>
  import('@/components/honey-form/HoneyFormDesigner')
const RuleList = () => import('@/components/rule-management/RuleList')
const RuleCard = () => import('@/components/rule-management/RuleCard')
const RuleDesigner = () => import('@/components/rule-management/RuleDesigner')
const BotTemplateList = () =>
  import('@/components/bot-template/BotTemplateList')

const BotTemplateCard = () =>
  import('@/components/bot-template/BotTemplateCard')
const BotCreation = () => import('@/components/bot-creation/BotCreation')
const BotAddition = () => import('@/components/bot-creation/BotAddition')
const IdentityList = () => import('@/components/identity/IdentityList')
const IdentityCreate = () => import('@/components/identity/IdentityCreate')
const ProfilesTreeView = () => import('@/components/profile/ProfilesTreeView')
const ProfileCreate = () => import('@/components/profile/ProfileCreate')
const ProposalDesigner = () =>
  import('@/components/bot-creation/ProposalDesigner')
const HiveList = () => import('@/components/hives/HiveList')
const HiveCreate = () => import('@/components/hives/HiveCreate')
const UserList = () => import('@/components/user-management/UserList.vue')
const RabbitMQ = () => import('@/components/admin/RabbitMQ')
const Camunda = () => import('@/components/admin/Camunda')
const Monitor = () => import('@/components/admin/Monitor')
const BeeKeeperLogs = () => import('@/components/admin/BeeKeeperLogs')
const PersonalAccessTokenList = () =>
  import('@/components/personal-access-tokens/PersonalAccessTokenList')
const PersonalAccessTokenCreate = () =>
  import('@/components/personal-access-tokens/PersonalAccessTokenCreate')
const Buzz = () => import('@/components/buzz/Buzz')
const ApiAutomationDesigner = () =>
  import('@/components/api-automation/ApiAutomationDesigner')
const BuzzAdmin = () => import('@/components/admin/BuzzAdmin')
const ApiAutomationList = () =>
  import('@/components/api-automation/ApiAutomationList')
const HiveBotList = () => import('@/components/hives/HiveBotList')
const HiveBotRegister = () => import('@/components/hives/HiveBotRegister')
const ApplicationSettings = () =>
  import('@/components/user-management/ApplicationSettings')
const JobSchedule = () => import('@/components/bot-scheduler/JobSchedule.vue')
const TasksHistory = () => import('@/components/task-lists/TasksHistory')
const TaskApprovalCard = () =>
  import('@/components/task-lists/TaskApprovalCard')
const ARCard = () => import('@/components/self-service/ARCard')
const ExecutionFocus = () => import('@/components/execution/ExecutionFocus')
const ProfileHistory = () => import('@/components/profile/ProfileHistory')
const RuleHistory = () => import('@/components/rule-management/RuleHistory')
const SyncUpFix = () => import('@/components/user-management/SyncUpFix')
const TulipMaintenance = () => import('@/components/admin/TulipMaintenance')
const TaskReportHome = () => import('@/components/task-lists/TaskReportHome')
const EmailTemplateList = () =>
  import('@/components/email-template/EmailTemplateList')
const EmailTemplateDesigner = () =>
  import('@/components/email-template/EmailTemplateDesigner')
const WorkflowVersionControl = () =>
  import('@/components/workflow-management/WorkflowVersionControl')
const TaskMaintenance = () => import('@/components/task-lists/TaskMaintenance')
const RoleMaintanance = () => import('@/components/admin/RolesVsPrivilege')
const InboundPackage = () =>
  import('@/components/package-management/InboundPackage')
const OutboundPackage = () =>
  import('@/components/package-management/OutboundPackage')
const SelfServiceDetail = () =>
  import('@/components/self-service/SelfServiceDetail')
const HiveDetail = () => import('@/components/hives/HiveDetail.vue')
const IdentitiesDetail = () => import('@/components/identity/IdentityDetail')
const HoneyFromDetail = () => import('@/components/honey-form/HoneyFormDetail')

function patchRouterMethod(router, methodName) {
  router['old' + methodName] = router[methodName]
  router[methodName] = async function(location) {
    return router['old' + methodName](location).catch(error => {
      if (error.name === 'NavigationDuplicated') {
        return this.currentRoute
      }
      throw error
    })
  }
}

Vue.use(Router)

const router = new Router({
  routes: [
    {
      path: '*',
      component: Error404
    },
    {
      path: '/',
      redirect: '/honey'
    },
    {
      path: '/login',
      name: 'login',
      component: LoginPage,
      beforeEnter: handleUnauthRoute
    },
    {
      path: '/honey/no-session',
      name: 'no-session',
      component: Error401
    },
    {
      path: '/honey/',
      component: MainLayout,
      beforeEnter: handleAuthRoute,
      meta: {
        breadcrumb: [{ name: 'Home', link: '/honey/' }]
      },
      // Todo: Fix all master detail links
      children: [
        {
          path: '',
          component: Home,
          name: 'home',
          meta: {
            breadcrumb: [{ name: 'Home', link: '/honey/' }]
          }
        },
        {
          path: 'service/:hexId',
          component: Home,
          name: 'service',
          meta: {
            breadcrumb: [{ name: 'Home', link: '/honey/' }]
          },
          props: true
        },
        {
          path: 'service-info/:hexId',
          component: Home,
          name: 'service-info',
          meta: {
            breadcrumb: [{ name: 'Home', link: '/honey/' }]
          },
          props: true
        },
        {
          path: 'service-ar-history/:hexId',
          component: Home,
          name: 'service-ar-history',
          meta: {
            breadcrumb: [{ name: 'Home', link: '/honey/' }]
          },
          props: true
        },
        {
          path: 'tasks',
          component: TaskListHome,
          name: 'tasks',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Task', link: '/honey/tasks/' }
            ]
          },
          children: [
            {
              path: ':hexId',
              component: TaskListHome,
              name: 'task-selected',
              meta: {
                breadcrumb: [
                  { name: 'Home', link: '/honey/' },
                  { name: 'Task', link: '/honey/tasks/' }
                ]
              },
              props: true
            }
          ]
        },
        {
          path: 'task-support',
          component: TaskReportHome,
          name: 'task-support',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              {
                name: 'Support Tasks',
                link: '/honey/task-report/'
              }
            ]
          }
        },
        {
          path: 'task-report',
          component: TasksHistory,
          name: 'task-report',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              {
                name: 'Task Report',
                link: '/honey/task-report/'
              }
            ]
          }
        },
        {
          path: 'task-action/:hexId',
          component: TaskApprovalCard,
          name: 'task-action',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Task', link: '/honey/tasks/' }
            ]
          },
          props: true
        },
        {
          path: 'automation-request/:hexId',
          component: ARCard,
          name: 'automation-request',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Automation Services', link: '/honey/self-services/' }
            ]
          },
          props: true
        },
        {
          path: 'dashboard',
          component: MyHoney,
          name: 'dashboard',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Dashboard', link: '/honey/dashboard/' }
            ]
          }
        },
        {
          path: 'bot-scheduler',
          component: BotScheduler, // Todo: This route does not seem to be used
          name: 'bot-scheduler'
        },
        {
          path: 'execution/:hexId',
          component: ExecutionFocus,
          name: 'execution',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              {
                name: 'Execution History',
                link: '/honey/executions/'
              }
            ]
          },
          props: true
        },
        {
          path: 'executions',
          component: Executions,
          name: 'executions',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              {
                name: 'Execution History',
                link: '/honey/executions/'
              }
            ]
          }
        },
        {
          path: 'task-history',
          component: TasksHistory,
          name: 'task-history',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              {
                name: 'Completed Task Reports',
                link: '/honey/task-history/'
              }
            ]
          }
        },
        {
          path: 'profile-history',
          component: ProfileHistory,
          name: 'profile-history',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              {
                name: 'Profiles',
                link: '/honey/profile-history/'
              }
            ]
          }
        },
        {
          path: 'rule-history',
          component: RuleHistory,
          name: 'rule-history',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              {
                name: 'Rule',
                link: '/honey/rule-history/'
              }
            ]
          }
        },
        {
          path: 'job-schedule',
          component: JobSchedule,
          name: 'job-schedule',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Job Schedule', link: '/honey/job-schedule/' }
            ]
          }
        },
        {
          path: 'dms',
          component: DMSHome,
          name: 'tulip-dms',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Document Management', link: '/honey/dms/' }
            ]
          }
        },
        {
          path: 'dms/:hexId/:fileName',
          component: DMSHome,
          name: 'tulip-dms-file',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Document Management', link: '/honey/dms/' }
            ]
          }
        },
        {
          path: 'liveness',
          component: TulipLiveness,
          name: 'liveness',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Landscape', link: '/honey/my-tulip/' }
            ]
          }
        },
        {
          path: 'my-tulip',
          component: MyTulip,
          name: 'my-tulip',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Landscape', link: '/honey/my-tulip/' }
            ]
          }
        },
        {
          path: 'self-services',
          component: SelfServiceList,
          name: 'self-service-list',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Automation Services', link: '/honey/self-services/' }
            ]
          },
          children: [
            {
              path: ':hexId',
              component: SelfServiceList,
              name: 'self-service-detail',
              meta: {
                breadcrumb: [
                  { name: 'Home', link: '/honey/' },
                  { name: 'Automation Services', link: '/honey/self-services/' }
                ]
              }
            }
          ]
        },
        {
          path: 'self-services/:hexId',
          component: SelfServiceDetail,
          name: 'self-service-card',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Automation Services', link: '/honey/self-services/' }
            ]
          }
        },
        {
          path: 'workflows/designer',
          component: WorkflowDesigner,
          name: 'workflow-designer',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey' },
              { name: 'Workflow', link: '/honey/workflows' },
              { name: 'Workflow Designer', link: '/honey/workflows/designer' }
            ]
          },
          props: true,
          children: [
            {
              path: 'create',
              component: WorkflowDesigner,
              name: 'workflow-designer-create',
              meta: {
                breadcrumb: [
                  { name: 'Home', link: '/honey/' },
                  { name: 'Workflow', link: '/honey/workflows' },
                  {
                    name: 'Workflow Designer',
                    link: '/honey/workflows/designer/create'
                  }
                ]
              },
              props: true
            },
            {
              path: ':hexId',
              component: WorkflowDesigner,
              name: 'workflow-designer-detail',
              meta: {
                breadcrumb: [
                  { name: 'Home', link: '/honey/' },
                  { name: 'Workflow', link: '/honey/workflows' },
                  {
                    name: 'Workflow Designer',
                    link: route =>
                      `/honey/workflows/designer/${route.params.hexId}`
                  }
                ]
              },
              props: true
            }
          ]
        },
        {
          path: 'workflows/:hexId',
          component: WorkflowCard,
          name: 'workflow-card',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Workflow', link: '/honey/workflows/' }
            ]
          }
        },
        {
          path: 'workflows',
          component: WorkflowList,
          name: 'workflow-list',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Workflow', link: '/honey/workflows/' }
            ]
          },
          children: [
            {
              path: ':hexId',
              component: WorkflowList,
              name: 'workflow-detail',
              meta: {
                breadcrumb: [
                  { name: 'Home', link: '/honey/' },
                  { name: 'Workflow', link: '/honey/workflows/' }
                ]
              }
            }
          ]
        },
        {
          path: 'workflows/history',
          component: WorkflowVersionControl,
          name: 'workflow-version',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Workflow', link: '/honey/workflows/' }
            ]
          },
          props: true,
          children: [
            {
              path: ':hexId',
              component: WorkflowVersionControl,
              name: 'workflow-version-control',
              meta: {
                breadcrumb: [
                  { name: 'Home', link: '/honey/' },
                  { name: 'Workflow', link: '/honey/workflows' },
                  {
                    name: 'Workflow Version History',
                    link: route =>
                      `/honey/workflows/history/${route.params.hexId}`
                  }
                ]
              },
              props: true
            }
          ]
        },
        {
          path: 'bot-card/:hexId',
          component: BotCard,
          name: 'bot-card',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Bot Card', link: '/honey/bot-card/' }
            ]
          }
        },
        {
          path: 'bots',
          component: BotList,
          name: 'bot-list',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Bots', link: '/honey/bots' }
            ]
          },
          children: [
            {
              path: ':hexId',
              component: BotList,
              name: 'bot-detail',
              meta: {
                breadcrumb: [
                  { name: 'Home', link: '/honey/' },
                  { name: 'Bots', link: '/honey/bots' }
                ]
              }
            }
          ]
        },
        {
          path: 'email-templates',
          component: EmailTemplateList,
          name: 'email-templates',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Email Templates', link: '/honey/email-templates/' }
            ]
          },
          children: [
            {
              path: ':hexId',
              component: EmailTemplateList,
              name: 'email-template-detail',
              meta: {
                breadcrumb: [
                  { name: 'Home', link: '/honey/' },
                  { name: 'Email Templates', link: '/honey/email-templates/' }
                ]
              }
            }
          ]
        },
        {
          path: 'email-templates/designer',
          component: EmailTemplateDesigner,
          name: 'email-template-designer',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Email Templates', link: '/honey/email-templates/' },
              {
                name: 'Email Templates',
                link: '/honey/email-templates/designer'
              }
            ]
          },
          props: true,
          children: [
            {
              path: 'create',
              component: EmailTemplateDesigner,
              name: 'email-template-designer-create',
              meta: {
                breadcrumb: [
                  { name: 'Home', link: '/honey/' },
                  { name: 'Email Templates', link: '/honey/email-templates/' },
                  {
                    name: 'Email Template Designer',
                    link: '/honey/email-templates/designer/create'
                  }
                ]
              },
              props: true
            },
            {
              path: ':hexId',
              component: EmailTemplateDesigner,
              name: 'email-template-designer-detail',
              meta: {
                breadcrumb: [
                  { name: 'Home', link: '/honey/' },
                  { name: 'Email Templates', link: '/honey/email-templates/' },
                  {
                    name: 'Email Template Designer',
                    link: route =>
                      `/honey/email-templates/designer/${route.params.hexId}`
                  }
                ]
              },
              props: true
            }
          ]
        },
        {
          path: 'honey-forms/:hexId',
          component: HoneyFromDetail,
          name: 'honey-form-card',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Forms', link: '/honey/honey-forms/' }
            ]
          }
        },
        {
          path: 'honey-forms',
          component: HoneyFormList,
          name: 'honey-forms',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Forms', link: '/honey/honey-forms/' }
            ]
          },
          children: [
            {
              path: ':hexId',
              component: HoneyFormList,
              name: 'honey-form-detail',
              meta: {
                breadcrumb: [
                  { name: 'Home', link: '/honey/' },
                  { name: 'Forms', link: '/honey/honey-forms/' }
                ]
              }
            }
          ]
        },
        {
          path: 'honey-forms/designer',
          component: HoneyFormDesigner,
          name: 'honey-form-designer',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Forms', link: '/honey/honey-forms/' },
              {
                name: 'Form Designer',
                link: '/honey/honey-forms/designer'
              }
            ]
          },
          props: true,
          children: [
            {
              path: 'create',
              component: HoneyFormDesigner,
              name: 'honey-form-designer-create',
              meta: {
                breadcrumb: [
                  { name: 'Home', link: '/honey/' },
                  { name: 'Forms', link: '/honey/honey-forms/' },
                  {
                    name: 'Form Designer',
                    link: '/honey/honey-forms/designer/create'
                  }
                ]
              },
              props: true
            },
            {
              path: ':hexId',
              component: HoneyFormDesigner,
              name: 'honey-form-designer-detail',
              meta: {
                breadcrumb: [
                  { name: 'Home', link: '/honey/' },
                  { name: 'Forms', link: '/honey/honey-forms/' },
                  {
                    name: 'Form Designer',
                    link: route =>
                      `/honey/honey-forms/designer/${route.params.hexId}`
                  }
                ]
              },
              props: true
            }
          ]
        },
        {
          path: 'rules/:hexId',
          component: RuleCard,
          name: 'rule-card',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Rules', link: '/honey/rules/' }
            ]
          }
        },
        {
          path: 'rules',
          component: RuleList,
          name: 'rule-list',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Rules', link: '/honey/rules/' }
            ]
          },
          children: [
            {
              path: ':hexId',
              component: RuleList,
              name: 'rule-detail',
              meta: {
                breadcrumb: [
                  { name: 'Home', link: '/honey/' },
                  { name: 'Rules', link: '/honey/rules/' }
                ]
              }
            }
          ]
        },
        {
          path: 'rules/designer',
          component: RuleDesigner,
          name: 'rule-designer',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Rules', link: '/honey/rules' },
              { name: 'Create Rule', link: '/honey/rules/designer' }
            ]
          },
          props: true,
          children: [
            {
              path: 'create',
              component: RuleDesigner,
              name: 'rule-designer-create',
              props: true,
              meta: {
                breadcrumb: [
                  { name: 'Home', link: '/honey' },
                  { name: 'Rules', link: '/honey/rules' },
                  {
                    name: 'Rule Designer',
                    link: '/honey/rules/designer/create'
                  }
                ]
              }
            },
            {
              path: ':hexId',
              component: RuleDesigner,
              name: 'rule-designer-detail',
              meta: {
                breadcrumb: [
                  { name: 'Home', link: '/honey/' },
                  { name: 'Rules', link: '/honey/rules' },
                  {
                    name: 'Rule Designer',
                    link: route => `/honey/rules/designer/${route.params.hexId}`
                  }
                ]
              },
              props: true
            }
          ]
        },
        {
          path: 'bot-templates/:hexId',
          component: BotTemplateCard,
          name: 'bot-template-card',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Bot Templates', link: '/honey/bot-templates' }
            ]
          }
        },
        {
          path: 'bot-templates',
          component: BotTemplateList,
          name: 'bot-template-list',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Bot Templates', link: '/honey/bot-templates' }
            ]
          },
          children: [
            {
              path: ':hexId',
              component: BotTemplateList,
              name: 'bot-template-detail',
              meta: {
                breadcrumb: [
                  { name: 'Home', link: '/honey/' },
                  { name: 'Bot Templates', link: '/honey/bot-templates' }
                ]
              }
            }
          ]
        },
        {
          path: 'email',
          component: EmailList,
          name: 'email-list',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Inbound Email Template', link: '/honey/email/' }
            ]
          },
          children: [
            {
              path: ':hexId',
              component: EmailList,
              name: 'email-detail',
              meta: {
                breadcrumb: [
                  { name: 'Home', link: '/honey/' },
                  { name: 'Email Template', link: '/honey/email/' }
                ]
              }
            }
          ]
        },
        {
          path: 'bot-addition',
          component: BotAddition,
          name: 'bot-addition',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey' },
              { name: 'Add Existing Bot', link: '/honey/bot-addition' }
            ]
          }
        },
        {
          path: 'add-bot',
          component: BotCreation,
          name: 'add-bot',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Create New Bot', link: '/honey/add-bot/' }
            ]
          }
        },
        {
          path: 'identities/create',
          component: IdentityCreate,
          name: 'identity-create',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Identities', link: '/honey/identities/' },
              { name: 'Create Identities', link: '/honey/identities/create/' }
            ]
          }
        },
        {
          path: 'identities/:hexId',
          component: IdentitiesDetail,
          name: 'identities-detail-card',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'identities', link: '/honey/identities/' }
            ]
          }
        },
        {
          path: 'identities',
          component: IdentityList,
          name: 'identity-list',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Identities', link: '/honey/identities/' }
            ]
          },
          children: [
            {
              path: ':hexId',
              component: IdentityList,
              name: 'identity-detail',
              meta: {
                breadcrumb: [
                  { name: 'Home', link: '/honey/' },
                  { name: 'Identities', link: '/honey/identities/' }
                ]
              }
            }
          ]
        },
        {
          path: 'profiles',
          component: ProfilesTreeView,
          name: 'profile-tree-view',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Profiles', link: '/honey/profiles/' }
            ]
          },
          children: [
            {
              path: ':hexId',
              component: ProfilesTreeView,
              name: 'profile-tree-detail',
              meta: {
                breadcrumb: [
                  { name: 'Home', link: '/honey/' },
                  { name: 'Profiles', link: '/honey/profiles/' }
                ]
              }
            }
          ]
          // beforeEnter: ensure('isAdmin')
        },
        {
          path: 'profiles/create',
          component: ProfileCreate,
          name: 'profile-create',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Create Profiles', link: '/honey/profiles/create/' }
            ]
          }
          // beforeEnter: ensure('isAdmin')
        },
        {
          path: 'proposal-designer',
          component: ProposalDesigner,
          name: 'proposal-designer',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Proposal Designer', link: '/honey/proposal-designer/' }
            ]
          }
        },
        {
          path: 'hives/create',
          component: HiveCreate,
          name: 'hive-create',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Automation Agent', link: '/honey/hives/' },
              { name: 'Create Agent', link: '/honey/hives/create/' }
            ]
          }
        },
        {
          path: 'hives/:hexId',
          component: HiveDetail,
          name: 'hive-detail-card',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Automation Agent', link: '/honey/hives/' }
            ]
          }
        },
        {
          path: 'hives',
          component: HiveList,
          name: 'hive-list',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Automation Agent', link: '/honey/hives/' }
            ]
          },
          children: [
            {
              path: ':hexId',
              component: HiveList,
              name: 'hive-detail',
              meta: {
                breadcrumb: [
                  { name: 'Home', link: '/honey/' },
                  { name: 'Automation Agent', link: '/honey/hives/' }
                ]
              }
            }
          ]
        },
        {
          path: 'users',
          component: UserList,
          name: 'user-list',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Users', link: '/honey/users/' }
            ]
          },
          children: [
            {
              path: ':hexId',
              component: UserList,
              name: 'user-detail',
              meta: {
                breadcrumb: [
                  { name: 'Home', link: '/honey/' },
                  { name: 'Users', link: '/honey/users/' }
                ]
              }
            }
          ]
        },
        {
          path: 'task-maintenance',
          component: TaskMaintenance,
          name: 'task-maintenance',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Task Maintenance', link: '/honey/task-maintenance/' }
            ]
          }
        },
        {
          path: 'role-maintenance',
          component: RoleMaintanance,
          name: 'role-maintenance',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Role Maintenance', link: '/honey/role-maintenance/' }
            ]
          }
        },
        {
          path: 'inbound-package',
          component: InboundPackage,
          name: 'inbound-package',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              {
                name: 'Inbound Package',
                link: '/honey/inbound-package/'
              }
            ]
          }
        },
        {
          path: 'outbound-package',
          component: OutboundPackage,
          name: 'outbound-package',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              {
                name: 'Outbound Package',
                link: '/honey/outbound-package/'
              }
            ]
          }
        },
        {
          path: 'rabbitmq',
          component: RabbitMQ,
          name: 'rabbitmq',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Queue Management', link: '/honey/rabbitmq/' }
            ]
          }
        },
        {
          path: 'camunda',
          component: Camunda,
          name: 'camunda',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Workflow Engine Management', link: '/honey/camunda/' }
            ]
          }
        },
        {
          path: 'monitor',
          component: Monitor,
          name: 'monitor',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Technical Monitoring', link: '/honey/monitor/' }
            ]
          }
        },
        {
          path: 'beekeeper-logs',
          component: BeeKeeperLogs,
          name: 'beekeeper-logs',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Logs', link: '/honey/beekeeper-logs/' }
            ]
          }
        },
        {
          path: 'tulip-settings',
          component: ApplicationSettings,
          name: 'honey-settings',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Preferences', link: '/honey/tulip-settings/' }
            ]
          }
        },
        {
          path: 'personal-access-tokens/create',
          component: PersonalAccessTokenCreate,
          name: 'personal-access-token-create',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              {
                name: 'Create Personal Access Token',
                link: '/honey/personal-access-tokens/'
              }
            ]
          }
        },
        {
          path: 'personal-access-tokens',
          component: PersonalAccessTokenList,
          name: 'personal-access-token-list',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              {
                name: 'Personal Access Token',
                link: '/honey/personal-access-tokens/'
              }
            ]
          },
          children: [
            {
              path: ':hexId',
              component: PersonalAccessTokenList,
              name: 'personal-access-token-detail',
              meta: {
                breadcrumb: [
                  { name: 'Home', link: '/honey/' },
                  {
                    name: 'Personal Access Token',
                    link: '/honey/personal-access-tokens/'
                  }
                ]
              }
            }
          ]
        },
        {
          path: 'buzz',
          component: Buzz, // Todo: This route does not seem to be used
          name: 'buzz'
        },
        {
          path: 'api-automations', // Todo: Under construction
          component: ApiAutomationList,
          name: 'api-automation-list',
          children: [
            {
              path: ':hexId',
              component: ApiAutomationList,
              name: 'api-automation-detail'
            }
          ]
        },
        {
          path: 'api-automations/designer', // Todo: Under construction
          component: ApiAutomationDesigner,
          name: 'api-automation-designer',
          props: true,
          children: [
            {
              path: 'create',
              component: ApiAutomationDesigner,
              name: 'api-automation-designer-create',
              props: true
            },
            {
              path: ':hexId',
              component: ApiAutomationDesigner,
              name: 'api-automation-designer-detail',
              props: true
            }
          ]
        },
        {
          path: 'buzz-admin',
          component: BuzzAdmin, // Todo: This route does not seem to be used
          name: 'buzz-admin'
        },
        {
          path: 'sync-up-fix',
          component: SyncUpFix,
          name: 'sync-up-fix',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Sync Up', link: '/honey/sync-up-fix/' }
            ]
          }
        },
        {
          path: 'tulip-maintenance',
          component: TulipMaintenance,
          name: 'tulip-maintenance',
          meta: {
            breadcrumb: [
              { name: 'Home', link: '/honey/' },
              { name: 'Tulip Maintenance', link: '/honey/tulip-maintenance/' }
            ]
          }
        },
        {
          path: 'hives/:hexId/hivebots/register', // Todo: To be deleted
          component: HiveBotRegister,
          name: 'hive-bot-register'
        },
        {
          path: 'hives/:hiveHexId/hivebots', // Todo: To be deleted
          component: HiveBotList,
          name: 'hive-bot-list',
          children: [
            {
              path: ':hiveBotHexId',
              component: HiveBotList,
              name: 'hive-bot-detail'
            }
          ]
        }
      ]
    }
  ],
  mode: 'history'
})

patchRouterMethod(router, 'push')
patchRouterMethod(router, 'replace')

export default router
